import { TypedEventEmitter } from "@dreamcam/web-sdk";
import { Env } from "./Env";
import { FeatureManager, FeatureName } from "./Features";
export class EnvironmentController extends TypedEventEmitter {
    async loadEnvironmentDescriptorsAsync() {
        const utcTimestamp = EnvironmentController._getCurrentUtcTimestamp();
        const allDescriptors = await this._loadLobbyEnvironmentsAsync(utcTimestamp);
        let defaultDescriptor = allDescriptors.find(x => x.isDefault === true);
        if (defaultDescriptor === undefined) {
            if (allDescriptors.length === 0) {
                defaultDescriptor = EnvironmentController.getBlackEnvironmentDescriptorOnError();
                allDescriptors.push(defaultDescriptor);
            }
            else {
                defaultDescriptor = allDescriptors[0];
                defaultDescriptor.isDefault = true;
            }
        }
        const eventDescriptor = allDescriptors.find(x => x.event !== undefined);
        // Fill lobbyEnvironmentLocalSettings:
        const result = { utcTimestamp, allDescriptors, defaultDescriptor, eventDescriptor };
        result.lobbyEnvironmentLocalSettings = this._readAndUpdateLocalSettings(result);
        // Calculate isNew attributes and make all URLs absolute:
        result.allDescriptors.map(x => {
            const codesSeen = result.lobbyEnvironmentLocalSettings && result.lobbyEnvironmentLocalSettings.newCodesSeen
                ? result.lobbyEnvironmentLocalSettings.newCodesSeen
                : [];
            x.isNew =
                x.newUntil !== undefined
                    && x.newUntil >= utcTimestamp
                    && codesSeen.find(c => c === x.code) === undefined;
            this._makeAbsoluteUrls(x);
        });
        // eventDescriptor is activated automatically, so isNew flag must be cleared:
        if (eventDescriptor !== undefined && eventDescriptor.isNew === true) {
            eventDescriptor.isNew = false;
        }
        // console.log(`%c${JSON.stringify(result, null, 2)}`, "background: lime");
        return result;
    }
    _makeAbsoluteUrls(environmentDescriptor) {
        environmentDescriptor.icon = Env.environment.getAbsUrl(environmentDescriptor.icon);
        if (environmentDescriptor.environment.skybox && environmentDescriptor.environment.skybox.photoDome)
            environmentDescriptor.environment.skybox.photoDome.url = Env.environment.getAbsUrl(environmentDescriptor.environment.skybox.photoDome.url);
        if (environmentDescriptor.environment.scene)
            environmentDescriptor.environment.scene.url = Env.environment.getAbsUrl(environmentDescriptor.environment.scene.url);
    }
    _readAndUpdateLocalSettings(envs) {
        let lobbySettings;
        const lobbySettingsJson = localStorage.getItem(EnvironmentController._lobbySettingsKey);
        if (lobbySettingsJson === null) {
            lobbySettings = { code: envs.defaultDescriptor.code };
        }
        else {
            try {
                lobbySettings = JSON.parse(lobbySettingsJson);
            }
            catch (e) {
                lobbySettings = { code: envs.defaultDescriptor.code };
            }
            // Find settled code in allDescriptors:
            const settledDescriptor = envs.allDescriptors.find(x => x.code === lobbySettings.code);
            // If the code is not found then use defaultDescriptor:
            if (settledDescriptor === undefined) {
                lobbySettings.code = envs.defaultDescriptor.code;
            }
        }
        // Check if locally stored event is outdated:
        if (lobbySettings.event !== undefined) {
            if (envs.utcTimestamp > lobbySettings.event.endDate) {
                lobbySettings.event = undefined;
            }
        }
        // Update event data:
        if (envs.eventDescriptor === undefined) {
            lobbySettings.event = undefined;
        }
        else {
            if (lobbySettings.event === undefined) {
                lobbySettings.event = {
                    code: envs.eventDescriptor.code,
                    endDate: envs.eventDescriptor.event.endDate,
                    isRejected: false,
                };
            }
            else {
                // Overwrite code and endDate, leave isRejected as is if it's the same event:
                const isSameEvent = lobbySettings.event.code === envs.eventDescriptor.code;
                lobbySettings.event.code = envs.eventDescriptor.code;
                lobbySettings.event.endDate = envs.eventDescriptor.event.endDate;
                if (!isSameEvent) {
                    lobbySettings.event.isRejected = false;
                }
            }
        }
        // Remove unnecessary newCodesSeen:
        if (lobbySettings.newCodesSeen) {
            const isNewDescriptor = (environmentDescr, currentUtcTimestamp) => {
                return environmentDescr.newUntil !== undefined && environmentDescr.newUntil > currentUtcTimestamp;
            };
            const foundAsNew = (code, descriptors, currentUtcTimestamp) => {
                return descriptors.find(x => x.code === code && isNewDescriptor(x, currentUtcTimestamp)) !== undefined;
            };
            // Remove seen codes that are not new anymore:
            const newCodesSeen = lobbySettings.newCodesSeen.filter(x => foundAsNew(x, envs.allDescriptors, envs.utcTimestamp));
            lobbySettings.newCodesSeen = newCodesSeen.length > 0 ? newCodesSeen : undefined;
        }
        localStorage.setItem(EnvironmentController._lobbySettingsKey, JSON.stringify(lobbySettings));
        // console.log(`%c${JSON.stringify(lobbySettings, null, 4)}`, "background: lime");
        return lobbySettings;
    }
    markEnvironmentCodeAsSeen(envs, code) {
        const lobbyEnvSettings = envs.lobbyEnvironmentLocalSettings;
        if (lobbyEnvSettings.newCodesSeen === undefined) {
            lobbyEnvSettings.newCodesSeen = [];
        }
        const foundDescriptor = envs.allDescriptors.find(x => x.code === code);
        if (foundDescriptor !== undefined
            && foundDescriptor.newUntil !== undefined
            && foundDescriptor.newUntil > envs.utcTimestamp
            && lobbyEnvSettings.newCodesSeen.find(x => x === code) === undefined) {
            lobbyEnvSettings.newCodesSeen.push(code);
        }
    }
    static getCurrentDescriptor(data) {
        if (data.lobbyEnvironmentLocalSettings === undefined) {
            console.error("LobbyEnvironmentLocalSettings not found.");
            return EnvironmentController.getBlackEnvironmentDescriptorOnError();
        }
        const lobbyEnvSettings = data.lobbyEnvironmentLocalSettings;
        let currentDescriptor = lobbyEnvSettings.event && !lobbyEnvSettings.event.isRejected
            ? data.allDescriptors.find(x => x.code === lobbyEnvSettings.event.code)
            : data.allDescriptors.find(x => x.code === lobbyEnvSettings.code);
        if (currentDescriptor === undefined) {
            console.error("EnvironmentDescriptor not found.");
            return EnvironmentController.getBlackEnvironmentDescriptorOnError();
        }
        return currentDescriptor;
    }
    async setLobbyEnvironmentAsync(view, envs, newDescriptor) {
        const currentDescriptor = EnvironmentController.getCurrentDescriptor(envs);
        // Initialization or changing the environment (undefined if changing to itself):
        const descriptor = newDescriptor === undefined
            ? currentDescriptor
            : newDescriptor.code === currentDescriptor.code ? undefined : newDescriptor;
        if (descriptor !== undefined) {
            const envChanged = view ? await this._setEnvironmentAsync(view, descriptor.environment) : true;
            if (envChanged) {
                // Update lobbyEnvSettings in local storage:
                const lobbyEnvSettings = envs.lobbyEnvironmentLocalSettings;
                lobbyEnvSettings.code = descriptor.code;
                if (lobbyEnvSettings.event && lobbyEnvSettings.event.code !== descriptor.code) {
                    // If event environment is changed to another one then mark the event as rejected:
                    lobbyEnvSettings.event.isRejected = true;
                }
                localStorage.setItem(EnvironmentController._lobbySettingsKey, JSON.stringify(lobbyEnvSettings));
                this.emit("onLobbyEnvironmentChanged", { environmentDescriptor: descriptor });
            }
        }
    }
    static _getCurrentUtcTimestamp() {
        return Math.floor(new Date().getTime() / 1000);
    }
    async setRoomEnvironmentAsync(view) {
        if (view) {
            await this._setEnvironmentAsync(view, EnvironmentController._getBlackEnvironment());
        }
    }
    async _setEnvironmentAsync(view, sceneEnvironment) {
        const result = await view.preloadEnvironmentAsync(sceneEnvironment);
        if (!result)
            return false;
        return await view.updateEnvironmentAsync(sceneEnvironment);
    }
    static _getBlackEnvironment() {
        return {
            skybox: {
                get color() { return FeatureManager.instance.isFeatureRequested(FeatureName.Passthrough) && FeatureManager.instance.isFeatureSupported(FeatureName.Passthrough) && FeatureManager.instance.isFeatureEnabled(FeatureName.Passthrough) ? "#00000000" : "#000000"; },
            },
        };
    }
    static getBlackEnvironmentDescriptorOnError() {
        return {
            name: "Black",
            code: "black",
            icon: Env.environment.defaultIcon.img,
            isDefault: true,
            environment: this._getBlackEnvironment(),
        };
    }
    async _loadLobbyEnvironmentsAsync(currentUtcTimestamp) {
        try {
            const response = await fetch(Env.lobby.environmentsUrl);
            let envs = await response.json();
            envs = envs.filter(x => x.event === undefined ||
                (currentUtcTimestamp >= x.event.startDate && currentUtcTimestamp <= x.event.endDate));
            return Object.assign([], envs);
        }
        catch (e) {
            console.error(e);
            return [EnvironmentController.getBlackEnvironmentDescriptorOnError()];
        }
    }
}
EnvironmentController._lobbySettingsKey = "lobby_environment";
